import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Alert, AlertDescription } from '../../ui/alert';
import { Plus } from 'lucide-react';

/**
 * Income Sources Step - All additional retirement income sources
 */
export default function IncomeSourcesStep({
  inputs,
  updateInput,
  setInputs,
  country,
  currencySymbol,
  statePensionEstimate
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Additional Retirement Income Sources</CardTitle>
          <CardDescription>
            Add all income sources beyond your portfolio withdrawals to get a complete retirement picture.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* State Pension / Social Security Section */}
          <div className="pt-4 border-t">
            <h3 className="text-lg font-semibold mb-4">
              {country === 'UK' ? 'State Pension' : 'Social Security'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {country === 'UK'
                ? 'Include UK State Pension in your retirement calculations (£11,502/year for 2024/25 with full contributions)'
                : 'Include US Social Security in your retirement calculations'}
            </p>

            <div className="space-y-4">
              {/* Main Person State Pension Toggle */}
              <div className="flex items-center space-x-3 p-4 bg-muted/50 rounded-lg border">
                <input
                  type="checkbox"
                  id="includeMainStatePension"
                  checked={inputs.includeStatePension}
                  onChange={(e) => updateInput('includeStatePension', e.target.checked)}
                  className="h-5 w-5"
                />
                <div className="flex-1">
                  <Label htmlFor="includeMainStatePension" className="cursor-pointer font-semibold text-base">
                    Include Your {country === 'UK' ? 'State Pension' : 'Social Security'}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {inputs.includeStatePension ? (
                      <>Estimated at {currencySymbol}{Math.round(statePensionEstimate).toLocaleString()}/year starting at age {inputs.statePensionAge}</>
                    ) : (
                      'Toggle on to include in retirement income calculations'
                    )}
                  </p>
                </div>
              </div>

              {/* Spouse State Pension Toggle - Only show if spouse income is enabled */}
              {inputs.spouseIncome?.included && (
                <div className="flex items-center space-x-3 p-4 bg-muted/50 rounded-lg border">
                  <input
                    type="checkbox"
                    id="includeSpouseStatePension"
                    checked={inputs.includeSpouseStatePension}
                    onChange={(e) => updateInput('includeSpouseStatePension', e.target.checked)}
                    className="h-5 w-5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="includeSpouseStatePension" className="cursor-pointer font-semibold text-base">
                      Include Spouse's {country === 'UK' ? 'State Pension' : 'Social Security'}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {inputs.includeSpouseStatePension ? (
                        <>Estimated at {currencySymbol}{Math.round(inputs.spouseIncome?.statePension || 0).toLocaleString()}/year starting at age {inputs.spouseIncome?.statePensionAge || 67}</>
                      ) : (
                        'Toggle on to include spouse\'s state pension in calculations'
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Add Buttons */}
          <div className="pt-4 border-t">
            <h3 className="text-lg font-semibold mb-4">Other Income Sources</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setInputs(prev => ({
                    ...prev,
                    rentalProperties: [...prev.rentalProperties, {
                      monthlyRent: 2000,
                      annualExpenses: 3000,
                      vacancyRate: 0.05,
                      startYear: 0,
                      endYear: 30,
                      inflationAdjusted: true
                    }]
                  }));
                }}
                className="text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Rental Property
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!inputs.partTimeWork) {
                    setInputs(prev => ({
                      ...prev,
                      partTimeWork: {
                        annualAmount: 20000,
                        startAge: inputs.retirementAge,
                        endAge: inputs.retirementAge + 5,
                        inflationAdjusted: true
                      }
                    }));
                  }
                }}
                disabled={inputs.partTimeWork !== null}
                className="text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Part-Time Work
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setInputs(prev => ({
                    ...prev,
                    annuities: [...prev.annuities, {
                      annualPayment: 12000,
                      startAge: 70,
                      endAge: 95,
                      type: 'fixed',
                      escalationRate: 0.025,
                      jointLife: false
                    }]
                  }));
                }}
                className="text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Annuity
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setInputs(prev => ({
                    ...prev,
                    otherIncome: [...prev.otherIncome, {
                      description: 'Other income',
                      annualAmount: 5000,
                      startAge: inputs.retirementAge,
                      endAge: inputs.lifeExpectancy,
                      inflationAdjusted: true
                    }]
                  }));
                }}
                className="text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Other Income
              </Button>
            </div>

            {/* Show message if no income sources added */}
            {inputs.rentalProperties.length === 0 &&
             !inputs.partTimeWork &&
             inputs.annuities.length === 0 &&
             inputs.otherIncome.length === 0 &&
             !inputs.spouseIncome?.included && (
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-xs">
                  <strong>No additional income sources configured.</strong>
                  <br />
                  Click the buttons above to add rental income, part-time work, annuities, or other income streams.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Rental Properties */}
          {inputs.rentalProperties.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Rental Properties</Label>
              {inputs.rentalProperties.map((property, index) => (
                <Card key={index} className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Monthly Rent ({currencySymbol})</Label>
                      <Input
                        type="number"
                        value={property.monthlyRent}
                        onChange={(e) => {
                          const newProperties = [...inputs.rentalProperties];
                          newProperties[index].monthlyRent = parseFloat(e.target.value);
                          setInputs(prev => ({ ...prev, rentalProperties: newProperties }));
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Annual Expenses ({currencySymbol})</Label>
                      <Input
                        type="number"
                        value={property.annualExpenses}
                        onChange={(e) => {
                          const newProperties = [...inputs.rentalProperties];
                          newProperties[index].annualExpenses = parseFloat(e.target.value);
                          setInputs(prev => ({ ...prev, rentalProperties: newProperties }));
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Vacancy Rate (%)</Label>
                      <Input
                        type="number"
                        value={property.vacancyRate * 100}
                        onChange={(e) => {
                          const newProperties = [...inputs.rentalProperties];
                          newProperties[index].vacancyRate = parseFloat(e.target.value) / 100;
                          setInputs(prev => ({ ...prev, rentalProperties: newProperties }));
                        }}
                        step="0.5"
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setInputs(prev => ({
                          ...prev,
                          rentalProperties: prev.rentalProperties.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-xs text-red-600"
                    >
                      Remove
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Part-Time Work */}
          {inputs.partTimeWork && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Part-Time Work During Retirement</Label>
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Annual Income ({currencySymbol})</Label>
                    <Input
                      type="number"
                      value={inputs.partTimeWork.annualAmount}
                      onChange={(e) => {
                        setInputs(prev => ({
                          ...prev,
                          partTimeWork: { ...prev.partTimeWork, annualAmount: parseFloat(e.target.value) }
                        }));
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Start Age</Label>
                    <Input
                      type="number"
                      value={inputs.partTimeWork.startAge}
                      onChange={(e) => {
                        setInputs(prev => ({
                          ...prev,
                          partTimeWork: { ...prev.partTimeWork, startAge: parseInt(e.target.value) }
                        }));
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End Age</Label>
                    <Input
                      type="number"
                      value={inputs.partTimeWork.endAge}
                      onChange={(e) => {
                        setInputs(prev => ({
                          ...prev,
                          partTimeWork: { ...prev.partTimeWork, endAge: parseInt(e.target.value) }
                        }));
                      }}
                      className="text-sm"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setInputs(prev => ({ ...prev, partTimeWork: null }))}
                    className="text-xs text-red-600"
                  >
                    Remove
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* Annuities */}
          {inputs.annuities.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Annuities</Label>
              {inputs.annuities.map((annuity, index) => (
                <Card key={index} className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Annual Payment ({currencySymbol})</Label>
                      <Input
                        type="number"
                        value={annuity.annualPayment}
                        onChange={(e) => {
                          const newAnnuities = [...inputs.annuities];
                          newAnnuities[index].annualPayment = parseFloat(e.target.value);
                          setInputs(prev => ({ ...prev, annuities: newAnnuities }));
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Start Age</Label>
                      <Input
                        type="number"
                        value={annuity.startAge}
                        onChange={(e) => {
                          const newAnnuities = [...inputs.annuities];
                          newAnnuities[index].startAge = parseInt(e.target.value);
                          setInputs(prev => ({ ...prev, annuities: newAnnuities }));
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={annuity.type}
                        onValueChange={(value) => {
                          const newAnnuities = [...inputs.annuities];
                          newAnnuities[index].type = value;
                          setInputs(prev => ({ ...prev, annuities: newAnnuities }));
                        }}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed</SelectItem>
                          <SelectItem value="escalating">Escalating</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Escalation Rate (%)</Label>
                      <Input
                        type="number"
                        value={annuity.escalationRate * 100}
                        onChange={(e) => {
                          const newAnnuities = [...inputs.annuities];
                          newAnnuities[index].escalationRate = parseFloat(e.target.value) / 100;
                          setInputs(prev => ({ ...prev, annuities: newAnnuities }));
                        }}
                        disabled={annuity.type === 'fixed'}
                        step="0.1"
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setInputs(prev => ({
                          ...prev,
                          annuities: prev.annuities.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-xs text-red-600"
                    >
                      Remove
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Other Income */}
          {inputs.otherIncome.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Other Income Sources</Label>
              {inputs.otherIncome.map((income, index) => (
                <Card key={index} className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        type="text"
                        value={income.description}
                        onChange={(e) => {
                          const newIncome = [...inputs.otherIncome];
                          newIncome[index].description = e.target.value;
                          setInputs(prev => ({ ...prev, otherIncome: newIncome }));
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Annual Amount ({currencySymbol})</Label>
                      <Input
                        type="number"
                        value={income.annualAmount}
                        onChange={(e) => {
                          const newIncome = [...inputs.otherIncome];
                          newIncome[index].annualAmount = parseFloat(e.target.value);
                          setInputs(prev => ({ ...prev, otherIncome: newIncome }));
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Start Age</Label>
                      <Input
                        type="number"
                        value={income.startAge}
                        onChange={(e) => {
                          const newIncome = [...inputs.otherIncome];
                          newIncome[index].startAge = parseInt(e.target.value);
                          setInputs(prev => ({ ...prev, otherIncome: newIncome }));
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">End Age</Label>
                      <Input
                        type="number"
                        value={income.endAge}
                        onChange={(e) => {
                          const newIncome = [...inputs.otherIncome];
                          newIncome[index].endAge = parseInt(e.target.value);
                          setInputs(prev => ({ ...prev, otherIncome: newIncome }));
                        }}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setInputs(prev => ({
                          ...prev,
                          otherIncome: prev.otherIncome.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-xs text-red-600"
                    >
                      Remove
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Spouse/Partner Income - Moved here from separate section */}
          <div className="space-y-3 pt-4 border-t">
            <Label className="text-sm font-semibold">Spouse/Partner Income (Optional)</Label>
            <Card className="p-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="includeSpouse"
                    checked={inputs.spouseIncome?.included || false}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setInputs(prev => ({
                          ...prev,
                          spouseIncome: {
                            included: true,
                            spouseAge: inputs.currentAge,
                            spouseRetirementAge: inputs.retirementAge,
                            statePension: country === 'UK' ? 11502 : 18000,
                            statePensionAge: country === 'UK' ? 66 : 67,
                            partTimeWork: 0,
                            partTimeStartAge: inputs.retirementAge,
                            partTimeEndAge: inputs.retirementAge + 5,
                            otherIncome: 0,
                            inflationAdjusted: true
                          }
                        }));
                      } else {
                        setInputs(prev => ({ ...prev, spouseIncome: null }));
                      }
                    }}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="includeSpouse" className="text-sm cursor-pointer font-semibold">
                    Include spouse/partner income in retirement plan
                  </Label>
                </div>

                {inputs.spouseIncome?.included && (
                  <div className="space-y-4 mt-4 pl-6 border-l-2 border-muted">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Spouse's Current Age</Label>
                        <Input
                          type="number"
                          value={inputs.spouseIncome.spouseAge}
                          onChange={(e) => {
                            setInputs(prev => ({
                              ...prev,
                              spouseIncome: { ...prev.spouseIncome, spouseAge: parseInt(e.target.value) }
                            }));
                          }}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Spouse Desired Retirement Age</Label>
                        <Input
                          type="number"
                          value={inputs.spouseIncome.spouseRetirementAge}
                          onChange={(e) => {
                            setInputs(prev => ({
                              ...prev,
                              spouseIncome: { ...prev.spouseIncome, spouseRetirementAge: parseInt(e.target.value) }
                            }));
                          }}
                          min={inputs.spouseIncome.spouseAge + 1}
                          max="100"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">
                          {country === 'UK' ? 'Spouse State Pension Age' : 'Spouse Social Security Age'}
                        </Label>
                        <Input
                          type="number"
                          value={inputs.spouseIncome.statePensionAge}
                          onChange={(e) => {
                            setInputs(prev => ({
                              ...prev,
                              spouseIncome: { ...prev.spouseIncome, statePensionAge: parseInt(e.target.value) }
                            }));
                          }}
                          min="62"
                          max="70"
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Spouse Annual State Pension ({currencySymbol})</Label>
                      <Input
                        type="number"
                        value={inputs.spouseIncome.statePension}
                        onChange={(e) => {
                          setInputs(prev => ({
                            ...prev,
                            spouseIncome: { ...prev.spouseIncome, statePension: parseFloat(e.target.value) }
                          }));
                        }}
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        {country === 'UK'
                          ? 'Full UK State Pension 2024/25: £11,502/year'
                          : 'Average US Social Security: ~$18,000-25,000/year'}
                      </p>
                    </div>

                    <div className="space-y-3 pt-2 border-t">
                      <Label className="text-xs font-semibold">Spouse Part-Time Work (Optional)</Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Annual Income ({currencySymbol})</Label>
                          <Input
                            type="number"
                            value={inputs.spouseIncome.partTimeWork}
                            onChange={(e) => {
                              setInputs(prev => ({
                                ...prev,
                                spouseIncome: { ...prev.spouseIncome, partTimeWork: parseFloat(e.target.value) }
                              }));
                            }}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Start Age</Label>
                          <Input
                            type="number"
                            value={inputs.spouseIncome.partTimeStartAge}
                            onChange={(e) => {
                              setInputs(prev => ({
                                ...prev,
                                spouseIncome: { ...prev.spouseIncome, partTimeStartAge: parseInt(e.target.value) }
                              }));
                            }}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">End Age</Label>
                          <Input
                            type="number"
                            value={inputs.spouseIncome.partTimeEndAge}
                            onChange={(e) => {
                              setInputs(prev => ({
                                ...prev,
                                spouseIncome: { ...prev.spouseIncome, partTimeEndAge: parseInt(e.target.value) }
                              }));
                            }}
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1 pt-2 border-t">
                      <Label className="text-xs">Spouse Other Annual Income ({currencySymbol})</Label>
                      <Input
                        type="number"
                        value={inputs.spouseIncome.otherIncome}
                        onChange={(e) => {
                          setInputs(prev => ({
                            ...prev,
                            spouseIncome: { ...prev.spouseIncome, otherIncome: parseFloat(e.target.value) }
                          }));
                        }}
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Any other guaranteed income (pensions, annuities, etc.)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
